/**
 * Deterministic randomness shared by the server and the app, so a day's shift (who comes in, where
 * the bugs are, the song's notes) is the same wherever it's built. Never Math.random for anything
 * that must replay.
 */

/** FNV-1a: a stable 32-bit hash of a string (seeds from ids and dates). */
export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** mulberry32: a small, fast generator of 0..1 from a 32-bit seed. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let x = Math.imul(a ^ (a >>> 15), 1 | a);
    x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

/** A seeded generator with the helpers games and content need. */
export function seeded(seed: string | number) {
  const next = mulberry32(typeof seed === 'string' ? hashString(seed) : seed);
  const int = (n: number) => Math.floor(next() * n);
  const pick = <T>(xs: readonly T[]): T => {
    if (!xs.length) throw new Error('seeded.pick from an empty list');
    return xs[int(xs.length)]!;
  };
  const shuffle = <T>(xs: readonly T[]): T[] => {
    const out = [...xs];
    for (let i = out.length - 1; i > 0; i--) {
      const j = int(i + 1);
      [out[i], out[j]] = [out[j]!, out[i]!];
    }
    return out;
  };
  return { next, int, pick, shuffle, seed: () => int(2 ** 31) };
}
export type Seeded = ReturnType<typeof seeded>;

/** A steady pseudo-random 0..1 for a number, cheap enough for a Reanimated worklet. */
export function hash01(k: number): number {
  'worklet';
  const s = Math.sin(k * 12.9898 + 78.233) * 43758.5453;
  return s - Math.floor(s);
}

/** Clamp to 0..1 (worklet-safe). */
export function clamp01(x: number): number {
  'worklet';
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
